<script setup>
import { computed } from 'vue'
import { useQueueStore } from '@state/stores/queue'
import { usePlaybackStore } from '@state/stores/playback'
import ShowService from './../services/show-service'
import modalService from '@/services/modal-service'
import { useAppearanceStore } from '@state/stores/appearance'
import { useAuthStore } from '@state/stores/auth'
import { useShowsStore } from '@state/stores/shows'
import { useQueueService } from '@composables/use-queue-service'

// Play family (Buttons §04): XL/L pill · disc 64/48/36 · bare glyph in rows.
// Disc glyph = 43% of the diameter, centroid-centred (the SVG carries the centring).
// BaseIcon's `size` prop only accepts the 16/20/24/32 grid, so `l` asks for the
// nearest valid step (32); the SCSS `$glyphs` map below then squares the
// rendered element down to the true 43%-of-64 target (28). That 32→28 shrink
// only has a visual effect on the Buffering state: `icon-play`/`icon-pause`
// are solid fills with no `stroke` attribute, so it's a no-op for them — only
// the stroked `icon-spinner` glyph needs the `@include iconStroke` correction
// below (see the SCSS) to keep its weight proportional at 28px.
const DISC_GLYPH = { l: 32, m: 20, s: 16 }

const props = defineProps({
    resource: {
        type: [Object, Array],
        default: () => ({}),
    },
    playlist: {
        type: Object,
        default: null,
    },
    appearance: {
        type: String,
        default: 'pill',
        // NOTE: inlined (not a module-scope const) — defineProps() cannot
        // reference locally declared `<script setup>` variables, they are
        // hoisted outside of setup(). See _base-icon.vue / _base-button.vue.
        validator: value => ['pill', 'disc', 'glyph'].includes(value),
    },
    // pill: xl | l (default l) · disc: l 64 | m 48 | s 36 (default l)
    size: {
        type: String,
        default: 'l',
        validator: value => ['xl', 'l', 'm', 's'].includes(value),
    },
})

const appearanceStore = useAppearanceStore()
const authStore = useAuthStore()
const queueStore = useQueueStore()
const playbackStore = usePlaybackStore()
const queueService = useQueueService()

const loggedIn = computed(() => authStore.loggedIn)

const isCurrent = computed(
    () =>
        (playbackStore.resource &&
            props.resource &&
            playbackStore.resource.type === props.resource.type &&
            playbackStore.resource.id === props.resource.id) ||
        (props.playlist && queueStore.playlistId === props.playlist.id)
)

const isBuffering = computed(() => isCurrent.value && playbackStore.status === 'loading')
const isPlaying = computed(() => isCurrent.value && playbackStore.status === 'playing')
const isMixLoaded = computed(() => playbackStore.resource && props.resource.id === playbackStore.resource.id)
const hasTarget = computed(() => Boolean((props.resource && props.resource.id) || (props.playlist && props.playlist.id)))

// Named playIcon (not `glyph`) so this dynamic binding can't be mistaken for a
// literal icon name by the svg-sprite parity scan (src/components/svg-sprite.unit.js) —
// it flags any all-lowercase `icon="…"` attribute value, static or bound.
const playIcon = computed(() => {
    if (isBuffering.value) return 'spinner'
    if (isPlaying.value) return 'pause'
    return 'play'
})

const title = computed(() => {
    if (isBuffering.value) return 'Loading'
    if (isPlaying.value) return 'Pause'
    return 'Play'
})

const ariaBusy = computed(() => (isBuffering.value ? 'true' : undefined))

// R15 (supersedes R3): every live BaseButtonPlay call site is a pill/glyph,
// never a disc, so THESE are the controls real users actually click while
// buffering. FR-6 requires a click during loading to still pause, exactly as
// today — but BaseButton's `loading` prop swallows clicks (its capture-phase
// guard) and sets aria-disabled, so the template deliberately does NOT pass
// `loading` to the pill/glyph BaseButton. `playIcon`/`title` above already
// switch to the spinner/"Buffering" independently of `loading`; `ariaBusy`
// sets `aria-busy` by hand and the buffering-spin SCSS rule below replicates
// BaseButton's own spinner animation manually, so the button stays a normal,
// fully clickable button throughout. The disc was always a raw <button> and
// needed no change — it has stayed clickable while buffering since Task 1.4
// round 1.

const classes = computed(() => [
    'base-button-play',
    `base-button-play-${props.appearance}`,
    `base-button-play-${props.size}`,
    !hasTarget.value && 'base-button-play-inactive',
    isPlaying.value && 'base-button-play-playing',
    isBuffering.value && 'base-button-play-buffering',
])

function increaseReach(id) {
    ShowService().increaseSessionReach(id)
    useShowsStore().increaseReach()
}

function handleClick() {
    if (props.playlist) return

    if (isMixLoaded.value) return queueService.pause()

    if (isPlaying.value || isBuffering.value) {
        queueService.pause()
    } else if (props.resource && props.resource.type === 'channel') {
        if (!loggedIn.value) {
            modalService.show('AUTH_REQUIRED_FOR_PLAYBACK')
            return
        }
        queueService.playRadioChannel(props.resource.id).catch(e => {
            appearanceStore.handleNotification({ message: e.message, type: 'warning' })
        })
    } else {
        if (!loggedIn.value) {
            modalService.show('AUTH_REQUIRED_FOR_PLAYBACK')
            return
        }
        queueService
            .playMix(props.resource)
            .then(() => {
                increaseReach(props.resource.show_id.id)
            })
            .catch(e => {
                appearanceStore.handleNotification({ message: e.message, type: 'warning' })
            })
    }
}
</script>

<template>
    <button
        v-if="appearance === 'disc'"
        :class="classes"
        :title="title"
        :aria-label="title"
        :aria-busy="ariaBusy"
        type="button"
        @click="handleClick"
    >
        <span class="disc">
            <BaseIcon
                :name="playIcon"
                :size="DISC_GLYPH[size] || DISC_GLYPH.l"
                :class="['glyph', { spin: isBuffering }]"
            />
        </span>
    </button>
    <BaseButton
        v-else-if="appearance === 'glyph'"
        :class="classes"
        variant="tertiary"
        :size="size"
        icon-only
        :icon="playIcon"
        :label="title"
        :aria-busy="ariaBusy"
        :active="isPlaying"
        @click="handleClick"
    />
    <BaseButton
        v-else
        :class="classes"
        variant="primary"
        :size="size === 'xl' ? 'xl' : 'l'"
        :icon="playIcon"
        :aria-busy="ariaBusy"
        :aria-label="title"
        @click="handleClick"
    >
        <slot>{{ title }}</slot>
    </BaseButton>
</template>

<style lang="scss" scoped>
@use 'sass:map';
@use '@design' as *;

$discs: ('l': 64px, 'm': 48px, 's': 36px);
// 43% of the diameter, rounded to the grid: 28 · 20 · 16.
$glyphs: ('l': 28px, 'm': 20px, 's': 16px);

.base-button-play-inactive {
    pointer-events: none;
    opacity: 0.3;
    filter: grayscale(1);
}

// R15: the pill/glyph BaseButton no longer receives `loading` (it would
// swallow clicks — see the R15 comment in the script, above `classes`), so
// its own built-in spinner animation never engages. Reach into the icon it
// renders and apply the exact same animation the global `.spin` utility
// class sets (`src/design/_motion-globals.scss`), so the spinner still
// visibly spins while Buffering. No `prefers-reduced-motion` override here
// either — matching `.spin`'s own documented exemption there ("the spinner
// is kept, it is information").
.base-button-play-buffering :deep(.base-button-icon) {
    animation: spin var(--motion-spinner) linear infinite;
}

.base-button-play-disc {
    display: inline-flex;
    padding: 0;
    color: $c-text-on-accent;
    cursor: pointer;
    background: transparent;
    border: 0;
    border-radius: $radius-full;

    @include transition(transform, fast);
    @include focusRing;

    @each $name, $diameter in $discs {
        &.base-button-play-#{$name} {
            @include square($diameter);

            .glyph {
                @include square(map.get($glyphs, $name));
            }
        }
    }

    // The 'l' disc's glyph is requested at 32 and squared down to 28px above (see
    // the script's DISC_GLYPH comment). Only the stroked Buffering spinner cares:
    // `vector-effect="non-scaling-stroke"` pins its 32-grid `stroke-width: 2` in
    // screen pixels, so without this it would read heavier than the FDS weight.
    &.base-button-play-l .glyph {
        @include iconStroke(28px);
    }

    &:hover .disc {
        background: $c-accent-hover;
    }

    // Same selector inside and out of the media query, so reduced motion wins the tie.
    &:active {
        transform: scale(0.97);

        @media (prefers-reduced-motion: reduce) {
            transform: none;
        }
    }
}

.disc {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    background: $c-accent-default;
    border-radius: $radius-full;

    @include transition(background-color);
}
</style>
